import React from 'react';

const MemoryTypes = ({ onSectionChange }) => {
  return (
    <div className="doc-content">
      <div className="doc-header">
        <h1>Memory Types</h1>
        <p className="doc-description">
          Understanding different types of memories in MemU and how they work together to create intelligent AI experiences.
        </p>
      </div>

      <div className="doc-section">
        <h2 id="overview">Overview</h2>
        <p>
          Modern LLM-powered AI agents utilize two distinct memory architectures - <strong>short-term</strong> and <strong>long-term</strong> memory systems - that work together to enable contextual understanding and personalized interactions.
        </p>

        <div className="memory-comparison">
          <div className="memory-type-card">
            <h4>Short-term Memory</h4>
            <p>
              This temporary memory system operates within the language model's context window throughout an active session. It enables fluid conversation flow and real-time task execution, though it faces limitations in storage capacity and disappears when the session ends.
            </p>
          </div>

          <div className="memory-type-card">
            <h4>Long-term Memory</h4>
            <p>
              In contrast, persistent memory utilizes external storage solutions - including vector databases, knowledge graphs, and file systems - to maintain user data, conversation history, and learned context across multiple sessions. This architecture enables semantic search, continuous learning, and intelligent information management, creating personalized user experiences that evolve over time.
            </p>
          </div>
        </div>

        <div className="info-callout">
          <i className="fa-solid fa-info-circle"></i>
          <p>
            MemU implements an agent-centric architecture that utilizes <strong>file-based storage systems</strong> for organizing and maintaining persistent memory.
          </p>
        </div>
      </div>

      <div className="doc-section">
        <h2 id="memory-units">Memory Units</h2>
        <p>
          Within the MemU architecture, we define <strong>memory items</strong> as the foundational building blocks for information storage and retrieval operations. These atomic units capture discrete pieces of knowledge, each containing a focused piece of information that typically ranges from one to two sentences.
        </p>

        <div className="info-box">
          <h4>💡 Key Concept</h4>
          <p>Each memory item functions as an indivisible information unit, carefully crafted to balance brevity with semantic richness for enhanced retrieval efficiency.</p>
        </div>
      </div>

      <div className="doc-section">
        <h2 id="memory-categories">Memory Categories</h2>
        <p>
          A fundamental aspect of MemU's design philosophy centers on <strong>file-based memory organization</strong>. We implement memory categories as organizational structures where individual memory items are grouped into dedicated document files based on their categorical classification. These category-based files function as logical containers within the MemU ecosystem, enabling efficient organization of related memories and streamlined retrieval processes.
        </p>

        <p>The system accommodates three distinct category types:</p>

        <div className="category-types">
          <div className="category-card">
            <h4><i className="fa-solid fa-cog"></i> System-defined Categories</h4>
            <p>
              Built-in categories designed for universal use cases, including <code>profile</code> for storing personal user information and <code>event</code> for capturing significant user experiences. Our experimental studies showcase the practical implementation and effectiveness of these categories in real-world memory management scenarios.
            </p>
          </div>

          <div className="category-card">
            <h4><i className="fa-solid fa-user"></i> User-defined Categories</h4>
            <p>
              Flexible categories that users can create to meet specialized requirements, such as establishing a <code>purchase</code> category for e-commerce assistant applications.
            </p>
          </div>

          <div className="category-card">
            <h4><i className="fa-solid fa-brain"></i> Clustered Categories</h4>
            <p>
              Dynamically generated categories that emerge through MemU's intelligent clustering algorithms, self-analysis, and adaptive learning processes, operating independently without manual configuration.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemoryTypes;

